import _ from "lodash";
import jwt_decode from "jwt-decode";
import moment from "moment";
import haversine from "haversine-distance";
import { v4 as uuid } from "uuid";
import { detect } from "detect-browser";

import { countryCodes } from "./country-codes";
import {
  API_BASE_URL,
  API_KEY as API_KEY_CONFIG,
  SOCKET_URL as SOCKET_URL_CONFIG,
} from "../lib/api/env";
import {
  getAuthToken as getStoredAuthToken,
  getRefreshToken as getStoredRefreshToken,
} from "../lib/api/tokenStore";
import { store } from "../redux";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
  showWarningToast,
} from "../components/Toast";
import { getProfile } from "../features/profile/profileStore";

import {
  dateFormat,
  dashboardDateFormat,
  filterDateFormat,
  NOTIFICATION_TIME,
  dateTimeFormat,
  UTC_TIME_FORMAT,
  UTC_MINUTE_FORMAT,
  CARD_EXPIRY_VALIDATION,
  listDateFormat,
  FALLBACK_IP_INFO,
} from "./constants";

import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { getConfig } from "../hooks/use-config";
import { fetchCountryStates } from "../components/HexMap/api-handler";

export const getFileExt = {
  PNG: "image/png",
  png: "image/png",
  JPEG: "image/jpeg",
  jpeg: "image/jpeg",
  JPG: "image/jpg",
  jpg: "image/jpg",
  PDF: "application/pdf",
  pdf: "application/pdf",
};

/**
 * These three used to read from the `/apps/init` response cached in
 * localStorage. That endpoint does not exist on the Sayagie backend, so they
 * now resolve from `lib/api/env.js`. Same values the new client uses.
 */
export const API_KEY = () => API_KEY_CONFIG;

export const API_URL = () => API_BASE_URL;

export const SOCKET_CONNECT_URL = () => SOCKET_URL_CONFIG;

export const getContentType = (key) => {
  return getFileExt[key.toUpperCase()];
};

export const sizeInMB = (sizeInBytes) =>
  (sizeInBytes / (1024 * 1024)).toFixed(2);

export const getAuthTokenExpiryTime = (authToken) => {
  const decodeAuth = jwt_decode(authToken);
  const { exp } = decodeAuth;

  const expiryTime = exp - Math.floor(Date.now() / 1000);

  return expiryTime;
};

/****** User Profiles ************/

/**
 * "Is someone signed in?" for the legacy screens — the header, AuthButton,
 * HexAuthWrapper and the empty states all gate on this.
 *
 * It used to read localStorage["user"], which only the retired password flow
 * ever wrote. Since the session+OTP flow stores its tokens in
 * `lib/api/tokenStore`, that made a signed-in user look signed out. There is
 * now one source of truth and this derives from it.
 *
 * Still returns a JSON string rather than a boolean, because several callers
 * do `JSON.parse(getActiveUser())` to read the refresh token.
 */
export const getActiveUser = () => {
  const refreshToken = getStoredRefreshToken();
  const authToken = getStoredAuthToken();

  if (!refreshToken && !authToken) return null;

  return JSON.stringify({ refreshToken });
};

/**
 * The bearer for the legacy axios wrapper.
 *
 * The old version read the token out of redux and force-logged-out against a
 * stored `tokenExpiry`. Expiry is no longer the client's job: `lib/api/client`
 * catches the 401, refreshes once and replays, which is both correct and
 * invisible to the user.
 */
export const getAuthToken = () => getStoredAuthToken() || "";

export const decodedJWTExpiry = (authToken) => {
  const decodeAuth = jwt_decode(authToken);
  const date = new Date(0);

  const { exp } = decodeAuth;
  date.setUTCSeconds(exp);

  return date;
};

export const getIdentityId = () => {
  const authToken = getAuthToken();
  if (authToken) {
    const decodedJwt = jwt_decode(authToken);
    return decodedJwt.uid;
  }

  return null;
};

export const hasOperation = (operation) => {
  const { authentication } = store.getState();
  const { operations } = authentication;

  return operations.includes(operation);
};

export const isApprovedUser = (token) => {
  const decodeAuth = jwt_decode(token);
  const { approved } = decodeAuth;
  return approved;
};

/**Country Utils */

export const getCountryCode = (key) => {
  const code = countryCodes.filter((country) => country.isoCode === key);

  return code.dialCode;
};

export const countryOptions = () => {
  const { publicAPIs } = store.getState();
  const { countryOptions } = publicAPIs;
  return countryOptions;
};

export const getCountry = (country) => {
  return countryOptions().find(
    ({ value, codeIso3 }) => value === country || codeIso3 === country
  );
};

export const getCurrencyByCountry = (country) => {
  return getCountry(country).currencyCode;
};

/** */

export const getConsumer = () => getProfile();

export const isJSON = (val) => {
  if (typeof val === "string" && val.length > 1) {
    const startChar = val[0];
    const endChar = val[val.length - 1];
    return ["{", "["].includes(startChar) && ["]", "}"].includes(endChar);
  }

  return false;
};

export const getErrorCode = (error) => {
  const { data } = error.response || {};
  const { errorCode } = data || { errorCode: "" };

  return errorCode;
};

export const handleError = (error) => {
  console.error(error);

  if (typeof error === "string") {
    const message = getConfig(error);
    return message || error;
  } else {
    const { data } = error.response || {};
    const { debugMessage, errorCode } = data || {
      debugMessage: "",
      errorCode: "",
    };

    if (debugMessage && errorCode) {
      const message = getConfig(errorCode);
      return message || debugMessage;
    } else if (data && data.error) {
      return data.error;
    } else {
      return "Something went wrong";
    }
  }
};

const getCurrency = (currency) => {
  const { currencies } = store.getState().publicAPIs;
  return findData(currencies, "code", currency);
};

export const formatCurrency = (amount, currency) => {
  if (currency === "null") {
    return;
  }

  var formatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  });

  return formatter.format(amount);
};

export const formatAmountDetails = ({ value, currency }) => {
  return formatCurrency(value, currency);
};

export const removeLocalStorageItems = (items) => {
  Array.isArray(items)
    ? items.forEach((item) => localStorage.removeItem(item))
    : localStorage.removeItem(items);
};

export const removeSessionStorageItems = (items) => {
  Array.isArray(items)
    ? items.forEach((item) => sessionStorage.removeItem(item))
    : sessionStorage.removeItem(items);
};

export const getStorageItem = (key) => {
  const item = localStorage.getItem(key);

  if (item) return JSON.parse(item);

  return null;
};

export const setStorageItem = (key, data) => {
  localStorage.setItem(key, JSON.stringify(data));
};

/** DEVICE ID */

export const saveDeviceId = () => {
  setStorageItem("deviceId", uuid());
};

export const getDeviceId = () => {
  return getStorageItem("deviceId");
};

/** END */

export const formatTime = (date, format) => {
  if (date) {
    return moment(date).format(format || "h:mm");
  } else {
    return moment().format(format || "h:mm");
  }
};

export const formatHourTime = (date) => {
  return moment(date).format("h:mm A");
};

export const parseISODate = (date) => {
  return moment.utc(date).zone(-360);
};

export const isCardExpired = (ExpiryMonth, ExpiryYear) => {
  const currentDate = moment();
  const cardData = moment(ExpiryMonth + ExpiryYear, CARD_EXPIRY_VALIDATION);

  return !(cardData.isValid() && currentDate < cardData.add(1, "months"));
};

export const formatDate = (date) => {
  return moment(date).format(dateFormat);
};

export const dashboardFormatDate = (date) => {
  return moment(date).format(dashboardDateFormat);
};

export const formatDateTime = (date) => {
  return moment(date).format(dateTimeFormat);
};

export const formatListDate = (dateTime, is24Hour) => {
  const date = removeTimeZone(dateTime);
  return moment(date).format(`DD-MM-YYYY ${!is24Hour ? "hh:mm A" : "HH:MM"}`);
};

export const formatListMomentDate = (date) => {
  return moment(date).format(listDateFormat);
};

export const formatFilterDate = (date) => {
  return moment(date).format(filterDateFormat);
};

export const format12HourTime = (date, time, withoutSeconds) => {
  const timeFormat = !withoutSeconds ? UTC_TIME_FORMAT : UTC_MINUTE_FORMAT;

  if (time) {
    const formattedDate = formatDate(date);
    const accumulatedDate = `${formattedDate} ${time}`;

    return moment(accumulatedDate).format(timeFormat);
  }

  return moment(date).format(timeFormat);
};

export const format24HourTime = (date, time) => {
  if (time) {
    const formattedDate = formatDate(date);
    const accumulatedDate = `${formattedDate} ${time}`;

    return moment(accumulatedDate).format("HH:mm:ss");
  }

  return moment(date).format("HH:mm:ss");
};

export const getLocaleUTCTime = (date, time) => {
  const formattedDate = formatDate(date);
  const accumulatedDate = `${formattedDate} ${time}`;

  return moment(accumulatedDate).utc().format(UTC_TIME_FORMAT);
};

export const getWeekDay = (date) => {
  let weekDay = moment(date).day();
  weekDay = weekDay === 0 ? 7 : weekDay;

  return weekDay;
};

export const formatRangePicker = (date) => {
  return moment(date).format(filterDateFormat);
};

export const formatTimeline = (time) => {
  return moment(time).format("lt");
};

export const filterDateRange = () => {
  const currentDate = moment();
  let prevDate = "";
  prevDate = currentDate.subtract(6, "months");
  prevDate = prevDate.format(filterDateFormat);
  return [prevDate, formatRangePicker(moment())];
};

export const filteredDateRange = () => {
  const currentDate = moment();
  let prevDate = "";
  prevDate = currentDate.subtract(6, "months");
  return [prevDate, moment()];
};

export const calculateDiscount = (amount, originalAmount, currency) => {
  const currencyItem = getCurrency(currency);

  return ((1 - amount / originalAmount) * 100).toFixed(currencyItem.precision);
};

export const GetParam = (paramKey) => {
  const [queryParams] = useSearchParams();
  const locationParams = useParams();

  if (Array.isArray(paramKey)) {
    let parameters = {};
    paramKey.forEach((key) => {
      parameters[key] = queryParams.get(key) || locationParams[key];
    });

    return parameters;
  } else {
    return queryParams.get(paramKey) || locationParams[paramKey];
  }
};

export const GetLocationState = () => {
  const location = useLocation();
  const locationState = location.state;

  return locationState || {};
};

export const clearLocationState = (locationState) => {
  if (!_.isEmpty(locationState)) {
    Object.keys(locationState).forEach((key) => {
      delete locationState[key];
    });
    window.history.replaceState(window.history.state, document.title);
  }
};

export const formatName = ({ firstName, lastName }) => {
  return `${firstName || ""} ${lastName || ""}`;
};

export const formatAddress = (address, shortAddress) => {
  const { city, address1, address2, countryCode, stateCode } = address || {};

  const country = getCountry(countryCode)?.label ?? countryCode ?? "";
  if (shortAddress) {
    return `${city}, ${country}`;
  }

  return `${address1}${
    address2 ? `, ${address2}` : ""
  }, ${city}, ${stateCode}, ${country}`;
};

export const formatDeliveryAmmount = ({ value, currency }) => {
  return formatCurrency(value, currency);
};

export const hasSession = () => {
  return !!localStorage.getItem("user");
};

const getMapItem = (address, type) => {
  const information = address.address_components.filter((item) =>
    item.types.includes(type)
  );

  if (information.length) {
    return information[0].short_name;
  }

  return "";
};

export const formatMapAddress = (address) => {
  const countryCode = getMapItem(address, "country");
  const region = getMapItem(address, "administrative_area_level_1");
  const city = getMapItem(address, "locality");
  const postalCode = getMapItem(address, "postal_code");

  const {
    geometry: { location },
  } = address;

  return {
    countryCode,
    stateCode: region,
    city,
    postalCode,
    address1: address.formatted_address,
    lat: location.lat,
    long: location.lng,
  };
};

export const getStateName = async (stateCode, countryCode) => {
  const stateResponse = await fetchCountryStates(countryCode);

  const stateName = stateResponse.data.states.find(
    ({ code }) => code === stateCode
  ).name;

  return stateName;
};

const TOAST_DURATION = NOTIFICATION_TIME * 1000;

export const showInfoNotification = ({ title, body }) => {
  showInfoToast(body, title || "Info", TOAST_DURATION);
};

export const showErrorNotification = ({ title, body }) => {
  showErrorToast(body, title || "Error", TOAST_DURATION);
};

export const showSuccessNotification = ({ title, body }) => {
  showSuccessToast(body, title || "Success", TOAST_DURATION);
};

export const showWarningNotification = ({ title, body }) => {
  showWarningToast(body, title || "Heads up", TOAST_DURATION);
};

export const ascendingSort = (details, key) => {
  return details.sort((a, b) => a[key] - b[key]);
};

export const descendingSort = (details, key) => {
  return details.sort((a, b) => b[key] - a[key]);
};

export const sumValuesofSingleArray = (details, key) => {
  return details.reduce((a, b) => a + b[key], 0);
};

export const showSuccessMessage = (content) => {
  showSuccessToast(content, "Success", TOAST_DURATION);
};

export const isImage = (url) => {
  return /\.(jpg|jpeg|png|JPG|JPEG|PNG)$/.test(url);
};

export const isValidIBAN = (iban) => {
  const ibanRegex =
    /^([A-Z]{2}[ '+'\\'+'-]?[0-9]{2})(?=(?:[ '+'\\'+'-]?[A-Z0-9]){9,30}$)((?:[ '+'\\'+'-]?[A-Z0-9]{3,5}){2,7})([ '+'\\'+'-]?[A-Z0-9]{1,3})?$/;
  return ibanRegex.test(iban);
};

export const phoneInstance = () => {
  const phoneUtil =
    require("google-libphonenumber").PhoneNumberUtil.getInstance();
  return phoneUtil;
};

/************************** Moment Utils **************************/

export const startOf = (startType, date) => {
  if (date) return moment(date).startOf(startType).toISOString();

  return moment().startOf(startType).toISOString();
};

export const endOf = (endType, date) => {
  if (date) return moment(date).endOf(endType).toISOString();

  return moment().endOf(endType).toISOString();
};

export const isoDate = (date) => {
  return date ? moment(date).toISOString() : moment().toISOString();
};

export const hoursDuration = (duration) => {
  return parseInt(duration.asHours());
};

export const minutesDuration = (duration) => {
  return parseInt(duration.asMinutes()) % 60;
};

export const removeTimeZone = (time) => {
  let date = new Date(time);
  let userTimezoneOffset = date.getTimezoneOffset() * 60000;
  return moment(new Date(date.getTime() - userTimezoneOffset));
};

export const secondsFrom = (time) => {
  return moment().diff(time, "seconds");
};

export const minutesFrom = (time) => {
  return moment().diff(time, "minutes");
};

export const hoursFrom = (time) => {
  return moment().diff(time, "hours");
};

export const timeSince = (dateTime) => {
  let interval = "Just Now";

  const time = removeTimeZone(dateTime);

  const minute = moment().diff(time, "minutes");
  if (minute >= 1) {
    interval = minute + ` minute${minute > 1 ? "s" : ""} ago`;
  }

  const hours = moment().diff(time, "hour");
  if (hours >= 1) {
    interval = hours + ` hour${hours > 1 ? "s" : ""} ago`;
  }

  const days = moment().diff(time, "days");
  if (days >= 1) {
    interval = days + ` day${days > 1 ? "s" : ""} ago`;
  }

  const weeks = moment().diff(time, "week");
  if (weeks >= 1) {
    interval = weeks + ` week${weeks > 1 ? "s" : ""} ago`;
  }

  const months = moment().diff(time, "month");

  if (months >= 1) {
    interval = months + ` month${months > 1 ? "s" : ""} ago`;
  }

  return interval;
};

export const makeDeepCopy = (detail) => {
  return JSON.parse(JSON.stringify(detail));
};

export const countDecimals = (value) => {
  if (Math.floor(value) === value) return 0;
  return value.toString().split(".")[1].length || 0;
};

const userLocale = () => {
  return navigator.language.replace("-", "_");
};

export const getParameter = (metaInformation, key) => {
  const parameters = metaInformation.filter((item) => item.name === key);

  if (parameters.length) {
    const localeBasedParameter = parameters.find(
      ({ locale }) => locale === userLocale()
    );

    return (
      localeBasedParameter ||
      parameters.find(({ locale }) => locale === "en_US")
    );
  }

  return null;
};

export const isSingleDotWord = (word) => {
  return !word.includes(" ") && word.includes(".");
};

export const defaultCountry = () => {
  const ipInfo = getStorageItem("ip-info");
  if (ipInfo) {
    return ipInfo.countryCode;
  }

  return "";
};

export const setSupplier = (supplier, saveInSessionStorage) => {
  if (!saveInSessionStorage) {
    localStorage.setItem("supplier-form-data", JSON.stringify(supplier));
  } else {
    sessionStorage.setItem("supplier-form-data", JSON.stringify(supplier));
  }
};

export const kilometers = (meters) => {
  const kilometers = (meters / 1000).toFixed(1);
  return `${+kilometers === 0 ? 0 : kilometers}`;
};

export const calculateDistance = ({ latitude, longitude }) => {
  const latLong = { latitude, longitude };
  let distance = 0;

  const currentLatLong = getCurrentLatLong();

  distance = haversine(currentLatLong, latLong);

  return kilometers(distance);
};

export const getCurrentLatLong = () => {
  const markerLocation = getStorageItem("marker-location");
  if (markerLocation) {
    return markerLocation;
  }

  // `/ip-info` is a 404 on the Sayagie backend, so this can legitimately be
  // absent. It used to be dereferenced unguarded and threw.
  const ipInfo = getStorageItem("ip-info");

  return ipInfo?.location || FALLBACK_IP_INFO.location;
};

export const splitFirstSpace = (str) => {
  return str.split(/\s(.+)/);
};

export const sumAll = (details, key) => {
  return details.reduce((accumulator, item) => accumulator + item[key], 0);
};

export const getBrowserInfo = () => {
  const browserInfo = detect();

  return browserInfo;
};

export const getDeviceHeaders = () => {
  const storedId = getDeviceId();
  const deviceId = storedId || uuid();

  !storedId && saveDeviceId(deviceId);

  const { os, version, type, name } = getBrowserInfo();
  const { origin } = window.location;

  const browserHeaders = {
    "x-platform-type": type,
    "x-platform-name": `${os}-${name}`,
    "x-platform-version": version,
    "x-platform-device-id": deviceId,
    "x-app-name": origin,
    "x-app-version": "1.0",
  };

  return browserHeaders;
};

export const capitalize = (word) => {
  const lowercase = word.toLowerCase();

  return lowercase.charAt(0).toUpperCase() + lowercase.slice(1);
};

export const setWidthSize = (resizeWidth, string) => {
  switch (true) {
    case resizeWidth < 350 && string.length > 10:
      return "width-100";
    case resizeWidth < 400 && string.length > 14:
      return "width-150";
    default:
      return "w-fit-content";
  }
};

export const findData = (details, key, value) => {
  return details.find((item) => item[key] === value);
};

export const uniqueArray = (a) =>
  [...new Set(a.map((o) => JSON.stringify(o)))].map((s) => JSON.parse(s));
